import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, AlertCircle, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import { TurnstileWidget, turnstileEnabled } from '../components/TurnstileWidget';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle, verify2FA } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [referralCode, setReferralCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFAPhone, setTwoFAPhone] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('email'); // 'email' or 'sms'
  const [passcode, setPasscode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  // Phone login state
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  // Check for referral code from URL or localStorage
  useEffect(() => {
    const urlRef = searchParams.get('ref');
    const storedRef = localStorage.getItem('referral_code');
    const storedName = localStorage.getItem('referrer_name');
    
    if (urlRef) {
      setReferralCode(urlRef);
      setIsLogin(false); // Switch to signup mode for referrals
    } else if (storedRef) {
      setReferralCode(storedRef);
      setReferrerName(storedName || '');
      setIsLogin(false);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Phone login
        if (loginMethod === 'phone') {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login-phone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone: formData.phone,
              password: formData.password
            })
          });
          
          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.detail || 'Login failed');
          }
          
          // Check if 2FA is required
          if (data.requires_2fa) {
            setRequires2FA(true);
            setTwoFAPhone(data.phone);
            setTwoFAMethod(data.method || 'sms');
            setLoading(false);
            return;
          }
          
          // Direct login (non-admin) - cookie is set by server
          window.location.href = '/dashboard';
          return;
        }
        
        // Email login
        const result = await login(formData.email, formData.password);
        
        // Check if 2FA is required
        if (result?.requires_2fa) {
          setRequires2FA(true);
          setTwoFAEmail(result.email);
          setTwoFAMethod('email');
          setLoading(false);
          return;
        }
        
        // Check if approval is required
        if (result?.requires_approval) {
          setError(result.message || 'Your account is pending approval.');
          setLoading(false);
          return;
        }
      } else {
        if (turnstileEnabled && !turnstileToken) {
          setError('Please complete the security check');
          setLoading(false);
          return;
        }
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            phone: formData.phone,
            role: 'homeowner',
            referral_code: referralCode || null,
            turnstile_token: turnstileToken
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Registration failed');
        }
        
        // Cookie is set by server - clear referral data, then complete the entry questionnaire
        localStorage.removeItem('referral_code');
        localStorage.removeItem('referrer_name');
        window.location.href = '/complete-entry';
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.message || (typeof err === 'string' ? err : 'Authentication failed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (twoFAMethod === 'sms' && twoFAPhone) {
        // Phone 2FA verification
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-phone-passcode`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            phone: formData.phone,
            code: passcode
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          // Handle Pydantic validation errors (array format) or string errors
          const errorDetail = Array.isArray(data.detail) 
            ? data.detail.map(e => e.msg).join(', ')
            : (data.detail || 'Invalid passcode');
          throw new Error(errorDetail);
        }
        
        // Cookie is set by server
        window.location.href = '/dashboard';
      } else {
        // Email 2FA verification
        await verify2FA(twoFAEmail, passcode);
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.message || (typeof err === 'string' ? err : 'Invalid passcode');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      let response;
      
      if (twoFAMethod === 'sms' && twoFAPhone) {
        // Resend via phone - re-trigger phone login
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login-phone`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: formData.phone,
            password: formData.password
          })
        });
      } else {
        // Resend via email
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-2fa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: twoFAEmail })
        });
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }
      
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      const errorMessage = err?.message || (typeof err === 'string' ? err : 'Failed to resend verification code');
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-neutral-50 to-neutral-100 flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-neutral-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                {twoFAMethod === 'sms' ? (
                  <Phone className="w-8 h-8 text-[#FF3B30]" />
                ) : (
                  <Lock className="w-8 h-8 text-[#FF3B30]" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
                Verify Your Identity
              </h1>
              <p className="text-neutral-500 mt-2">
                {twoFAMethod === 'sms' ? (
                  <>We&apos;ve sent a 6-digit code to your phone<br />
                  <span className="font-medium text-neutral-700">***-***-{twoFAPhone}</span></>
                ) : (
                  <>We&apos;ve sent a 6-digit code to<br />
                  <span className="font-medium text-neutral-700">{twoFAEmail}</span></>
                )}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div>
                <Label htmlFor="passcode" className="text-neutral-700 font-medium">
                  Enter Verification Code
                </Label>
                <div className="mt-2">
                  <Input
                    id="passcode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ''))}
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl"
                    required
                    autoFocus
                    data-testid="2fa-passcode-input"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-2">Code expires in 10 minutes</p>
              </div>

              <Button
                type="submit"
                disabled={loading || passcode.length !== 6}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
                data-testid="verify-2fa-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleResend2FA}
                disabled={resendLoading}
                className="w-full h-12 rounded-xl border-neutral-200 hover:bg-neutral-50 font-medium"
                data-testid="resend-2fa-btn"
              >
                {resendLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-600 rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : resendSuccess ? (
                  <span className="flex items-center gap-2 text-green-600">
                    <Mail className="w-5 h-5" />
                    Code sent!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Send code again
                  </span>
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setPasscode('');
                  setError('');
                  setTwoFAPhone('');
                  setTwoFAEmail('');
                  setTwoFAMethod('email');
                }}
                className="w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
              >
                ← Back to login
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-neutral-50 to-neutral-100 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8"
        >
          {/* Logo */}
          <div className="text-center">
            <Link to="/" className="inline-block">
              <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>
                <span className="text-[#0A0A0A]">Spin</span>
                <span className="text-[#FF3B30]">ser</span>
              </h1>
            </Link>
            <h2 className="mt-6 text-2xl font-semibold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="mt-2 text-neutral-600">
              {isLogin ? 'Sign in to access your dashboard' : 'Start winning amazing home upgrades'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Referral Banner */}
          {referralCode && !isLogin && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-green-800 font-medium text-sm">
                  {referrerName ? `${referrerName} invited you!` : 'You were referred!'}
                </p>
                <p className="text-green-600 text-xs">Sign up to get bonus entries</p>
              </div>
            </div>
          )}

          {/* Social login */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-neutral-200 hover:bg-neutral-50"
              onClick={loginWithGoogle}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-white via-neutral-50 to-neutral-100 text-neutral-500">
                {isLogin ? 'or continue with' : 'or continue with email'}
              </span>
            </div>
          </div>

          {/* Login Method Toggle (only for login mode) */}
          {isLogin && (
            <div className="flex justify-center gap-2 p-1 bg-neutral-100 rounded-xl">
              <button
                type="button"
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  loginMethod === 'email' 
                    ? 'bg-white text-neutral-900 shadow-sm' 
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                data-testid="login-method-email"
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('phone')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  loginMethod === 'phone' 
                    ? 'bg-white text-neutral-900 shadow-sm' 
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
                data-testid="login-method-phone"
              >
                <Phone className="w-4 h-4" />
                Phone
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name" className="text-neutral-700">Full Name</Label>
                  <div className="mt-1 relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      required={!isLogin}
                      data-testid="name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-neutral-700">Phone (optional)</Label>
                  <div className="mt-1 relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Email Input (shown when login method is email or in signup mode) */}
            {(loginMethod === 'email' || !isLogin) && (
              <div>
                <Label htmlFor="email" className="text-neutral-700">Email</Label>
                <div className="mt-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                    required={loginMethod === 'email' || !isLogin}
                    data-testid="email-input"
                  />
                </div>
              </div>
            )}

            {/* Phone Input (shown when login method is phone in login mode) */}
            {isLogin && loginMethod === 'phone' && (
              <div>
                <Label htmlFor="login-phone" className="text-neutral-700">Phone Number</Label>
                <div className="mt-1 relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input
                    id="login-phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="login-phone-input"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">Enter the phone number linked to your account</p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-neutral-700">Password</Label>
                {isLogin && (
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#FF3B30] hover:text-[#DC2626] font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 rounded-xl"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {!isLogin && <TurnstileWidget onVerify={setTurnstileToken} />}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? 'Sign in' : 'Create account'}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          {/* Toggle */}
          <p className="text-center text-neutral-600">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-[#FF3B30] font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3B30]/10 to-transparent" />
        <img
          src="https://static.prod-images.emergentagent.com/jobs/7626ece3-51a5-4a98-8e4d-5a4ba64801df/images/c8295a197636f981cdf6d21ba009d78148b5d8b38c83bc62463ad085fbeeccdd.png"
          alt="Modern home"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-lg font-medium mb-2">Join thousands of homeowners</p>
          <h3 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
            Win free home upgrades worth up to $10,000
          </h3>
        </div>
      </div>
    </div>
  );
};

export default Login;
